import { HOST_URL } from '../../config'
import { HttpStatus } from '../../constants/http-status.enum'
import type { ApiResponse } from '../index'
import type { IClientUID } from '../clients'

export interface ISettings {
    programMode: number
    ntc1MaxValue: number
    ntc1MinValue: number
    waterValue: number
    iceValue: number
    relayCooldown: number
    pumpPwmDuty: number
    minH2oPiezo: number
    minCo2Piezo: number
    pumpMaxTime: number
    piezo1Calib: number
    piezo2Calib: number
    boilerMaxHeat: number
    boilerMinHeat: number
    boilerEnable: number
    wifiEnable: number
    ignoreH2oPiezo: number
    ignoreCo2Piezo: number
    oneLiquidLevel: number
    oneLiquidLevelDelay: number
    liquidLevelDisabled: number
    pumpStartDelay: number
    pumpStopDelay: number
    ntc2MaxValue: number
    ntc2MinValue: number
    fanMaxWorkTime: number
    fanCoolTime: number
    circulationDelayStop: number
    circulationInterval: number
    circulationWorkTime: number
    circulationEnabled: number
    ambientMonitorEnabled: number
}

export enum SettingType {
    programMode = 0,
    ntc1MaxValue = 1,
    ntc1MinValue = 2,
    waterValue = 3,
    iceValue = 4,
    fanPwmDuty = 5,
    relayCooldown = 6,
    pumpPwmDuty = 7,
    minH2oPiezo = 8,
    minCo2Piezo = 9,
    pumpMaxTime = 10,
    piezo1Calib = 11,
    piezo2Calib = 12,
    boilerMaxHeat = 13,
    boilerMinHeat = 14,
    boilerEnable = 15,
    wifiEnable = 16,
    ignoreH2oPiezo = 17,
    ignoreCo2Piezo = 18,
    oneLiquidLevel = 22,
    oneLiquidLevelDelay = 23,
    fanDisabled = 24,
    liquidLevelDisabled = 25,
    pumpStartDelay = 26,
    pumpStopDelay = 27,
    ntc2MaxValue = 28,
    ntc2MinValue = 29,
    fanMaxWorkTime = 30,
    fanCoolTime = 31,
    circulationDelayStop = 32,
    circulationInterval = 33,
    circulationWorkTime = 34,
    circulationEnabled = 35,
    ambientMonitorEnabled = 36,
}

export enum ProgramMode {
    AluminiumBlock = 0,
    IceBank = 1,
}

export interface ISettingUpdate {
    type: SettingType
    value: number
}

export async function getClientSettings(clientId: IClientUID): Promise<ApiResponse<ISettings>> {
    const response = await fetch(`${HOST_URL}settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId }),
    })
    const result = (await response.json()) as ApiResponse<ISettings>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function updateClientSettings(clientId: IClientUID, updates: ISettingUpdate[]): Promise<ApiResponse<void>> {
    const response = await fetch(`${HOST_URL}settings`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId, updates }),
    })
    const result = (await response.json()) as ApiResponse<void>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function restoreClientSettings(clientId: IClientUID): Promise<ApiResponse<void>> {
    const response = await fetch(`${HOST_URL}settings`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId }),
    })
    const result = (await response.json()) as ApiResponse<void>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}
